import { useState, useEffect } from 'react'
import EventEmitter from 'eventemitter3';
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  Select,
  MenuItem,
} from '@mui/material';

import './App.css'
import config from './config.js';

const events = new EventEmitter();

window.addEventListener('message', (msg) => {
  events.emit('message', msg);
});

function postToIframe(type, data) {
  const iframe = document.getElementById('vitals-embed');
  if (iframe && iframe.contentWindow) {
    iframe.contentWindow.postMessage({ type, data }, '*');
  }
}

const styles = {
  buttonWrapper: {
    display: 'flex',
    justifyContent: 'center',
  },
  label: {
    fontWeight: 'bold',
  },
  root: {
    boxSizing: 'border-box',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '1rem',
  }
};

const skipPagesInitial = {
  startOnVitalsMeasurement: false,
  skipIntro: false,
  skipTerms: false,
  skipConsent: false,
  skipInstructions1: false,
  skipInstructions2: false,
  skipInstructions3: false,
  skipDisclaimer: false,
  skipInstructions: false,
  useClinicLogo: false,
  skipGwDisclaimer: false,
  skipMdGwIntro: false,
};

function App() {
  const [apiKey, setApiKey] = useState(sessionStorage.getItem('apiKey') || '');
  const [userId, setUserId] = useState(sessionStorage.getItem('userId') || '');
  const [state, setState] = useState(sessionStorage.getItem('state') || '');
  const [firstName, setFirstName] = useState(sessionStorage.getItem('firstName') || 'Test');
  const [lastName, setLastName] = useState(sessionStorage.getItem('lastName') || 'Patient');
  const [birthDate, setBirthDate] = useState(sessionStorage.getItem('birthDate') || '1990-01-01');
  const [clinic_id, setClinicId] = useState(sessionStorage.getItem('clinic_id') || '');
  const [apiUrl, setApiUrl] = useState(sessionStorage.getItem('apiUrl') || config.DEFAULT_API_URL);
  const [proUrl, setProUrl] = useState(sessionStorage.getItem('proUrl') || config.DEFAULT_PRO_URL);
  const [proType, setProType] = useState(sessionStorage.getItem('proType') || config.DEFAULT_PRO_TYPE);
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [notifcationId, setNotificationId] = useState('');
  const [vitals, setVitals] = useState('');
  const [skipPages, setSkipPages] = useState(skipPagesInitial);
  const [hideUserManual, setHideUserManual] = useState(false);
  const [useClinicLogo, setUseClinicLogo] = useState(false);
  const [useRearCamera, setUseRearCamera] = useState(false);
  const [useCpu, setUseCpu] = useState(false);
  const [useZoom, setUseZoom] = useState(false);

  useEffect(() => {
    const eventListener = (msg) => {

      console.log('eventListener to parent window', msg.data, msg.source, token);
      if (msg.data.type === 'error') {
        console.log('error', msg.data.data);
        setError(msg.data.data);
      }
      if (msg.data.type === 'readyForToken') {
        console.log('readyForToken', token);
        const appConfig = {
          ...skipPages,
          hideUserManual,
          useClinicLogo,
          useRearCamera,
          useCpu,
          useZoom,
        };
        postToIframe('appConfig', JSON.stringify(appConfig));
        postToIframe('token', token);
      }
      if (msg.data.type === 'results') {
        console.log('results', msg.data.data);
        setVitals(msg.data.data);
      }

    }

    postToIframe('token', token);

    events.on('message', eventListener);
    return () => {
      events.off('message', eventListener);
    }
  }, [hideUserManual, skipPages, token, useClinicLogo, useRearCamera, useCpu, useZoom]);

  async function launchWithNotification(userIdToSend) {
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`,
    }
    const response = await fetch(`${apiUrl}/users/${userIdToSend}/notifications`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        clinic_id,
        notification_type: proType,
        trigger_text: false,
        pre_auth: true,
      }),
    });
    const data = await response.json();
    if (data.error) {
      const e = new Error(data.message);
      e.code = data.statusCode;
      throw e;
    }
    setToken(data.pre_auth_token);
    setNotificationId(data.id);
  }

  async function createUserAndLaunch() {
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`,
    }
    try {
      const user = await (await fetch(`${apiUrl}/users/shadow`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          clinic_id,
          email: `test+${Math.random()}@test.com`,
          first_name: firstName,
          last_name: lastName,
          birth_date: birthDate,
          state,
        }),
      })).json();
      if (user.error) {
        const e = new Error(user.message);
        e.code = user.statusCode;
        throw e;
      }
      setUserId(user.id);
      sessionStorage.setItem('userId', user.id);
      await launchWithNotification(user.id);
    } catch (e) {
      setError(`${e.message}${e.code ? ` (${e.code})` : ''}`);
    }
  }

  async function launchWithUserId() {
    try {
      await launchWithNotification(userId);
    } catch (e) {
      setError(`${e.message}${e.code ? ` (${e.code})` : ''}`);
    }
  }

  console.log('embedder', proUrl, proType, `${proUrl}/embed/${notifcationId}`);

  const handleChangeSkipPages = (e) => {
    setSkipPages((prevState) => {
      return { ...prevState, [e.target.name]: e.target.checked };
    });
  };

  const handleChangeHideUserManual = (e) => {
    setHideUserManual(e.target.checked);
  };

  const handleChangeUseClinicLogo = (e) => {
    setUseClinicLogo(e.target.checked);
  };

  const handleChangeUseRearCamera = (e) => {
    setUseRearCamera(e.target.checked);
  };

  const handleChangeUseCpu = (e) => {
    setUseCpu(e.target.checked);
  };

  const handleChangeUseZoom = (e) => {
    setUseZoom(e.target.checked);
  };

  function getHostObj(url) {
    return config.hosts.find((host) => host.api_url === url);
  }

  function getUiObj(url) {
    return getHostObj(apiUrl).ui_urls.find((o) => o.url === url);
  }

  return (
    <div style={styles.root}>
      {!token ? (
        <>
        <h1>Vitals Embedder</h1>
        <div>
          <div>
            <Select value={apiUrl} onChange={(e) => {
              setApiUrl(e.target.value);
              sessionStorage.setItem('apiUrl', e.target.value);
              setProUrl(getHostObj(e.target.value).ui_urls[0].url);
              sessionStorage.setItem('proUrl', getHostObj(e.target.value).ui_urls[0].url);
              setProType(getHostObj(e.target.value).ui_urls[0].type);
              sessionStorage.setItem('proType', getHostObj(e.target.value).ui_urls[0].type);
            }}>
              {config.hosts.map((host) => (
                <MenuItem key={host.api_url} value={host.api_url}>{host.api_url}</MenuItem>
              ))}
            </Select>
            <Select value={proUrl} onChange={(e) => {
              setProUrl(e.target.value);
              sessionStorage.setItem('proUrl', e.target.value);
              const hostObj = getHostObj(apiUrl);
              const uiObj = getUiObj(e.target.value);
              console.log('hostObj', hostObj, 'uiObj', uiObj);
              const type = uiObj.type;
              setProType(type);
              sessionStorage.setItem('proType', type);
            }}>
              {getHostObj(apiUrl).ui_urls.map((o) => (
                <MenuItem key={o.url} value={o.url}>{o.url} - {o.type}</MenuItem>
              ))}
            </Select>
          </div>
        </div>
        {error ? (
          <div style={{ color: 'red' }}>
            {error}
          </div>
        ) : ''}
        <div>
          <FormGroup sx={{ paddingBottom: 4 }}>
            <FormControlLabel
              control={<Checkbox checked={skipPages.startOnVitalsMeasurement} onChange={handleChangeSkipPages} name="startOnVitalsMeasurement" />}
              label="Start on Vitals Measurement"
            />
            <FormControlLabel
              control={<Checkbox checked={skipPages.skipIntro} onChange={handleChangeSkipPages} name="skipIntro" />}
              label="Skip Intro"
            />
            {(proType === 'VITAL-CORE' || proType === 'VITAL-CORE-RR') && (
              <>
                <FormControlLabel
                  control={<Checkbox checked={skipPages.skipTerms} onChange={handleChangeSkipPages} name="skipTerms" />}
                  label="Skip Terms & Conditions (VC only)"
                />
                <FormControlLabel
                  control={<Checkbox checked={skipPages.skipConsent} onChange={handleChangeSkipPages} name="skipConsent" />}
                  label="Skip Consent to Record (VC only)"
                />
                <FormControlLabel
                  control={<Checkbox checked={skipPages.skipInstructions1} onChange={handleChangeSkipPages} name="skipInstructions1" />}
                  label="Skip Instructions 1 (VC only)"
                />
                <FormControlLabel
                  control={<Checkbox checked={skipPages.skipInstructions2} onChange={handleChangeSkipPages} name="skipInstructions2" />}
                  label="Skip Instructions 2 (VC only)"
                />
                <FormControlLabel
                  control={<Checkbox checked={skipPages.skipInstructions3} onChange={handleChangeSkipPages} name="skipInstructions3" />}
                  label="Skip Instructions 3 (VC only)"
                />
                <FormControlLabel
                  control={<Checkbox checked={hideUserManual} onChange={handleChangeHideUserManual} name="hideUserManual" />}
                  label="Hide User Manual (VC only)"
                />
              </>
            )}
            {proType === 'VITAL-TRAC' && (
              <>
                <FormControlLabel
                  control={<Checkbox checked={skipPages.skipDisclaimer} onChange={handleChangeSkipPages} name="skipDisclaimer" />}
                  label="Skip Disclaimer (VT only)"
                />
                <FormControlLabel
                  control={<Checkbox checked={skipPages.skipInstructions} onChange={handleChangeSkipPages} name="skipInstructions" />}
                  label="Skip Instructions (VT only)"
                />
                <FormControlLabel
                  control={<Checkbox checked={skipPages.skipGwDisclaimer} onChange={handleChangeSkipPages} name="skipGwDisclaimer" />}
                  label="Skip General Wellness Disclaimer (VT only)"
                />
                <FormControlLabel
                  control={<Checkbox checked={skipPages.skipMdGwIntro} onChange={handleChangeSkipPages} name="skipMdGwIntro" />}
                  label="Skip MD GW Intro (VT only)"
                />
                <FormControlLabel
                  control={<Checkbox checked={useClinicLogo} onChange={handleChangeUseClinicLogo} name="useClinicLogo" />}
                  label="Use Clinic Logo (VT only)"
                />
                <FormControlLabel
                  control={<Checkbox checked={useRearCamera} onChange={handleChangeUseRearCamera} name="useRearCamera" />}
                  label="Use Rear Camera (VT only)"
                />
                <FormControlLabel
                  control={<Checkbox checked={useCpu} onChange={handleChangeUseCpu} name="useCpu" />}
                  label="Use CPU (VT only)"
                />
                <FormControlLabel
                  control={<Checkbox checked={useZoom} onChange={handleChangeUseZoom} name="useZoom" />}
                  label="Use Zoom (VT only)"
                />
              </>
            )}
          </FormGroup>
          <div>
            <label>
              API Key:
              <input
                value={apiKey}
                onChange={(e) => { 
                  setApiKey(e.target.value);
                  sessionStorage.setItem('apiKey', e.target.value);
                }}
                style={{ width: '300px', marginBottom: '10px', marginLeft: 10 }}
              />
            </label>
          </div>
          <div>
            <label>
              Clinic ID:
              <input
                value={clinic_id}
                onChange={(e) => { 
                  setClinicId(e.target.value);
                  sessionStorage.setItem('clinic_id', e.target.value);
                }}
                style={{ width: '300px', marginBottom: '10px', marginLeft: 10 }}
              />
            </label>
          </div>
          <hr style={{ width: '100%', margin: '16px 0' }} />
          <div style={{ display: 'flex', gap: '40px' }}>
            <div>
              <h3>Create User</h3>
              <div>
                <label>
                  First Name:
                  <input
                    value={firstName}
                    onChange={(e) => {
                      setFirstName(e.target.value);
                      sessionStorage.setItem('firstName', e.target.value);
                    }}
                    style={{ width: '200px', marginBottom: '10px', marginLeft: 10 }}
                  />
                </label>
              </div>
              <div>
                <label>
                  Last Name:
                  <input
                    value={lastName}
                    onChange={(e) => {
                      setLastName(e.target.value);
                      sessionStorage.setItem('lastName', e.target.value);
                    }}
                    style={{ width: '200px', marginBottom: '10px', marginLeft: 10 }}
                  />
                </label>
              </div>
              <div>
                <label>
                  State:
                  <input
                    value={state}
                    onChange={(e) => {
                      setState(e.target.value);
                      sessionStorage.setItem('state', e.target.value);
                    }}
                    placeholder="e.g. AZ"
                    style={{ width: '200px', marginBottom: '10px', marginLeft: 10 }}
                  />
                </label>
              </div>
              <div>
                <label>
                  Birth Date:
                  <input
                    type="date"
                    value={birthDate}
                    onChange={(e) => {
                      setBirthDate(e.target.value);
                      sessionStorage.setItem('birthDate', e.target.value);
                    }}
                    style={{ width: '200px', marginBottom: '10px', marginLeft: 10 }}
                  />
                </label>
              </div>
              <div style={styles.buttonWrapper}>
                <button
                  disabled={!apiKey || !clinic_id || !state}
                  onClick={createUserAndLaunch}
                >
                  Create User & Launch
                </button>
              </div>
            </div>
            <div style={{ borderLeft: '1px solid #ccc', paddingLeft: '40px' }}>
              <h3>Specify User ID</h3>
              <div>
                <label>
                  User ID:
                  <input
                    value={userId}
                    onChange={(e) => {
                      setUserId(e.target.value);
                      sessionStorage.setItem('userId', e.target.value);
                    }}
                    style={{ width: '200px', marginBottom: '10px', marginLeft: 10 }}
                  />
                </label>
              </div>
              <div style={styles.buttonWrapper}>
                <button
                  disabled={!apiKey || !clinic_id || !userId}
                  onClick={launchWithUserId}
                >
                  Launch
                </button>
              </div>
            </div>
          </div>
        </div>
        </>
      ) : (
        <div style={{ flexGrow: 1, width: '100%' }}>
          <div>
            {vitals ? `${vitals.HR ? 'HR: ' + vitals.HR : ''} ${vitals.BR && vitals.BR > 0 ? 'BR: ' + vitals.BR : ''}` : ''}
          </div>
          <iframe
            width="100%"
            height="100%"
            id="vitals-embed"
            style={{
              width: '100%',
              height: '100%',
            }}
            src={`${proUrl}/embed/${notifcationId}`}
            allow={`camera ${proUrl};`}
          ></iframe>
        </div>
      )}
    </div>
  )
}


export default App
